import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { useLang } from '../contexts/LanguageContext';
import axios from 'axios';

// const API = `http://localhost:8080/desertnimbus/backend/api`;
 const API = `/api`;

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: '', org: '', email: '', interest: t.contact.interestOptions[0], message: '' });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: t.contact.toastMissingTitle, description: t.contact.toastMissingBody });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact.php`, { ...form, lang }, { timeout: 20000 });
      setSent(true);
      toast({ title: t.contact.toastSuccessTitle, description: t.contact.toastSuccessBody });
      setTimeout(() => setSent(false), 4000);
      setForm({ name: '', org: '', email: '', interest: t.contact.interestOptions[0], message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      toast({ title: 'Transmission failed', description: String(detail).slice(0, 220) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 md:py-40 border-t border-cream/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-mono text-[11px] tracking-[0.35em] text-[#c99a3d] mb-6">{t.contact.kicker}</div>
            <h2 className="font-serif text-cream font-light text-[42px] md:text-[62px] leading-[1.05] tracking-tight">
              {t.contact.titleA}<br />
              <span className="italic text-[#c99a3d]">{t.contact.titleAccent}</span>
            </h2>
            <p className="mt-8 text-cream/70 text-[16px] leading-[1.85] max-w-[440px]">{t.contact.body}</p>

            <div className="mt-14 space-y-4 border-t border-cream/10 pt-8">
              <Info label={t.contact.location} value={t.contact.locationV} />
              <Info label={t.contact.coords} value={t.contact.coordsV} />
              <Info label={t.contact.channel} value={t.contact.channelV} accent />
            </div>
          </div>

          <div className="md:col-span-7">
            <form onSubmit={submit} className="border border-cream/10 rounded-sm p-8 md:p-10 bg-[#0d1526]/60 backdrop-blur-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t.contact.name} value={form.name} onChange={(v) => onChange('name', v)} placeholder={t.contact.namePh} />
                <Field label={t.contact.org} value={form.org} onChange={(v) => onChange('org', v)} placeholder={t.contact.orgPh} />
                <Field label={t.contact.email} type="email" value={form.email} onChange={(v) => onChange('email', v)} placeholder={t.contact.emailPh} />
                <div className="flex flex-col">
                  <label className="font-mono text-[11px] tracking-[0.28em] text-cream/60 mb-3">{t.contact.interest}</label>
                  <div className="flex flex-wrap gap-2">
                    {t.contact.interestOptions.map((o) => (
                      <button
                        type="button"
                        key={o}
                        onClick={() => onChange('interest', o)}
                        className={`text-[11px] tracking-[0.15em] px-3 py-2 rounded-full border transition-all ${
                          form.interest === o ? 'bg-[#c99a3d] border-[#c99a3d] text-[#0a1220]' : 'border-cream/20 text-cream/70 hover:border-cream/40'
                        }`}
                        style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col">
                <label className="font-mono text-[11px] tracking-[0.28em] text-cream/60 mb-3">{t.contact.message}</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => onChange('message', e.target.value)}
                  placeholder={t.contact.messagePh}
                  className="bg-transparent border-b border-cream/20 focus:border-[#c99a3d] outline-none text-cream text-[16px] py-3 resize-none transition-colors placeholder:text-cream/30"
                  style={{ fontFamily: "'Fraunces', serif" }}
                />
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <div className="font-mono text-[10px] tracking-[0.3em] text-cream/50">{t.contact.disclosure}</div>
                <button
                  type="submit"
                  disabled={sent || submitting}
                  className="group inline-flex items-center gap-3 bg-[#ebe6d5] text-[#0a1220] rounded-full px-7 py-4 font-mono text-[11px] tracking-[0.28em] hover:bg-white transition-all disabled:opacity-70"
                >
                  {sent ? (
                    <>{t.contact.sent} <CheckCircle2 className="w-4 h-4" strokeWidth={1.6} /></>
                  ) : submitting ? (
                    <>SENDING…</>
                  ) : (
                    <>{t.contact.send} <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.6} /></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Info = ({ label, value, accent }) => (
  <div className="flex justify-between items-baseline gap-4">
    <span className="font-mono text-[10px] tracking-[0.3em] text-cream/50">{label}</span>
    <span className={`font-serif text-sm ${accent ? 'text-[#c99a3d]' : 'text-cream'}`}>{value}</span>
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div className="flex flex-col">
    <label className="font-mono text-[11px] tracking-[0.28em] text-cream/60 mb-3">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-transparent border-b border-cream/20 focus:border-[#c99a3d] outline-none text-cream text-[16px] py-3 transition-colors placeholder:text-cream/30"
      style={{ fontFamily: "'Fraunces', serif" }}
    />
  </div>
);

export default Contact;
